import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Info, ScrollText } from "lucide-react";
import {
  qrtRevenueByCompany,
  qrtDownloadRevenueCsv,
  qrtDownloadHistoryCsv,
  qrtSetPlanRevenueExcluded,
} from "../../apis/qrtAdmin";
import { StatCard, Spinner, Pager, fmtMoney } from "./qrtUi";

const PAGE_SIZE = 5;

const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const rangeFromDays = (days) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (Math.max(Number(days) || 30, 1) - 1));
  return { from: toIso(from), to: toIso(to) };
};

const PRESETS = [
  { id: "today", label: "Today", days: 1 },
  { id: "week", label: "Week", days: 7 },
  { id: "month", label: "Month", days: 30 },
  { id: "3_month", label: "3 months", days: 90 },
  { id: "year", label: "Year", days: 365 },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom" },
];

const money2 = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;

const SCENARIOS = [
  { id: "marketing", label: "Marketing" },
  { id: "marketing_lite", label: "Marketing lite" },
  { id: "utility", label: "Utility" },
  { id: "authentication", label: "Authentication" },
];

const withScenario = (row, category) => {
  const leftover = money2(Math.max(0, (row.leftover != null
    ? row.leftover
    : (row.wallet_base || 0) - (row.wallet_spent || 0))));
  const leftoverIf = money2(row.leftover_pockets?.[category] ?? leftover * (row.markup_categories?.[category]?.take_rate || 0));
  const actual = money2((row.wallet_markup || 0) + (row.plans_counted ? row.plans_base : 0));
  const cat = row.markup_categories?.[category] || {};
  return {
    ...row,
    category,
    leftover,
    leftover_if: leftoverIf,
    profit: actual,
    potential_profit: money2(actual + leftoverIf),
    take_rate_pct: cat.take_rate_pct,
    markup_per_msg: cat.markup_per_msg,
    sell_price: cat.sell_price,
    markup_source: cat.source,
  };
};

const QrtRevenue = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState("month");
  const [from, setFrom] = useState(rangeFromDays(30).from);
  const [to, setTo] = useState(rangeFromDays(30).to);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyUser, setBusyUser] = useState(null);
  const [openInfo, setOpenInfo] = useState(null);
  const [downloading, setDownloading] = useState("");
  const [query, setQuery] = useState("");
  const [scenario, setScenario] = useState("marketing");
  const [page, setPage] = useState(0);

  const params = useMemo(
    () => (preset === "all" ? {} : { from, to }),
    [preset, from, to]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    qrtRevenueByCompany(params)
      .then((res) => {
        if (cancelled) return;
        setRows(res.data?.data?.rows || []);
        setError("");
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || "Failed to load revenue");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [params]);

  const applyPreset = (id) => {
    setPreset(id);
    setPage(0);
    if (id === "custom" || id === "all") return;
    const p = PRESETS.find((x) => x.id === id);
    if (!p?.days) return;
    const next = rangeFromDays(p.days);
    setFrom(next.from);
    setTo(next.to);
  };

  const togglePlans = async (row) => {
    const excluded = row.plans_counted; // unchecking = exclude
    setBusyUser(row.user_id);
    // optimistic
    setRows((prev) =>
      prev.map((r) =>
        r.user_id === row.user_id
          ? {
              ...r,
              plans_counted: !excluded,
              profit: money2((r.wallet_markup || 0) + (!excluded ? r.plans_base : 0)),
            }
          : r
      )
    );
    try {
      await qrtSetPlanRevenueExcluded(row.user_id, excluded);
    } catch (_) {
      // revert
      setRows((prev) =>
        prev.map((r) =>
          r.user_id === row.user_id
            ? {
                ...r,
                plans_counted: excluded,
                profit: money2((r.wallet_markup || 0) + (excluded ? r.plans_base : 0)),
              }
            : r
        )
      );
      window.alert("Could not save the change — try again");
    } finally {
      setBusyUser(null);
    }
  };

  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const stamp = preset === "all" ? "all-time" : `${from}_to_${to}`;

  const downloadReport = async () => {
    setDownloading("report");
    try {
      const res = await qrtDownloadRevenueCsv({ ...params, category: scenario });
      saveBlob(res.data, `blutec-revenue-${scenario}-${stamp}.csv`);
    } catch (_) {
      window.alert("Failed to download the revenue report");
    } finally {
      setDownloading("");
    }
  };

  const downloadHistory = async () => {
    setDownloading("history");
    try {
      const res = await qrtDownloadHistoryCsv(params);
      saveBlob(res.data, `blutec-payment-history-${stamp}.csv`);
    } catch (_) {
      window.alert("Failed to download the payment history");
    } finally {
      setDownloading("");
    }
  };

  const viewed = useMemo(
    () => (rows || []).map((r) => withScenario(r, scenario)),
    [rows, scenario]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return viewed;
    return viewed.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q)
    );
  }, [viewed, query]);

  const pageRows = useMemo(() => {
    const start = page * PAGE_SIZE;
    return visible.slice(start, start + PAGE_SIZE);
  }, [visible, page]);

  const totals = useMemo(() => {
    const acc = {
      wallet_base: 0, wallet_paid: 0, plans_base: 0, plans_paid: 0,
      wallet_markup: 0, leftover: 0, leftover_if: 0, profit: 0, potential_profit: 0, excluded: 0,
    };
    for (const r of viewed) {
      acc.wallet_base += r.wallet_base;
      acc.wallet_paid += r.wallet_paid;
      if (r.plans_counted) {
        acc.plans_base += r.plans_base;
        acc.plans_paid += r.plans_paid;
      } else {
        acc.excluded += 1;
      }
      acc.wallet_markup += r.wallet_markup;
      acc.leftover += r.leftover || 0;
      acc.leftover_if += r.leftover_if || 0;
      acc.profit += r.profit;
      acc.potential_profit += r.potential_profit;
    }
    Object.keys(acc).forEach((k) => { acc[k] = money2(acc[k]); });
    return acc;
  }, [viewed]);

  if (error && !rows) return <div className="ping-banner red">{error}</div>;

  return (
    <div>
      <div className="qrt-page-head">
        <div>
          <h2>Blutec revenue</h2>
          <p>
            Actual profit is markup already earned on messages sent + counted plans.
            The type buttons only apply to leftover wallet that has not been spent yet.
          </p>
        </div>
        <div className="grow" />
        <div className="qrt-inline">
          <button
            type="button"
            className="ping-btn ping-btn-ghost"
            onClick={downloadHistory}
            disabled={downloading !== ""}
          >
            <ScrollText size={14} />
            {downloading === "history" ? "Preparing…" : "Download full history"}
          </button>
          <button
            type="button"
            className="ping-btn ping-btn-green"
            onClick={downloadReport}
            disabled={downloading !== ""}
          >
            <Download size={14} />
            {downloading === "report" ? "Preparing…" : "Download report"}
          </button>
        </div>
      </div>

      <div className="qrt-card" style={{ marginBottom: 14 }}>
        <div className="qrt-card-head" style={{ flexWrap: "wrap", gap: 10 }}>
          <div className="qrt-range-picks">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={preset === p.id ? "on" : ""}
                onClick={() => applyPreset(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset !== "all" && (
            <div className="qrt-inline">
              <label style={{ fontSize: 12 }}>
                From{" "}
                <input
                  type="date"
                  value={from}
                  onChange={(e) => { setPreset("custom"); setFrom(e.target.value); setPage(0); }}
                />
              </label>
              <label style={{ fontSize: 12 }}>
                To{" "}
                <input
                  type="date"
                  value={to}
                  onChange={(e) => { setPreset("custom"); setTo(e.target.value); setPage(0); }}
                />
              </label>
            </div>
          )}
          <div className="qrt-range-picks" style={{ margin: 0 }}>
            {SCENARIOS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={scenario === s.id ? "on" : ""}
                onClick={() => { setScenario(s.id); setPage(0); }}
              >
                If leftover as {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="qrt-load-wrap">
        {loading && (
          <div className="qrt-load-mask" aria-live="polite">
            <Spinner />
          </div>
        )}

        <div className="qrt-stats">
          <StatCard label="Wallet revenue" value={fmtMoney(totals.wallet_base)}
            sub={`collected ${fmtMoney(totals.wallet_paid)} with GST + fee`} />
          <StatCard label="Plan revenue (counted)" value={fmtMoney(totals.plans_base)}
            sub={totals.excluded > 0
              ? `${totals.excluded} compan${totals.excluded === 1 ? "y" : "ies"} not counted`
              : "all companies counted"} />
          <StatCard label="Actual profit" value={fmtMoney(totals.profit)}
            sub={`markup already earned ${fmtMoney(totals.wallet_markup)} + counted plans`} />
          <StatCard label="Leftover in wallets" value={fmtMoney(totals.leftover)}
            sub="recharge not spent yet — filter applies only here" />
          <StatCard label={`If leftover as ${scenario.replace("_", " ")}`} value={fmtMoney(totals.leftover_if)}
            sub={`actual + this leftover = ${fmtMoney(totals.potential_profit)}`} tone="blue" />
        </div>

        <div className="qrt-card">
          <div className="qrt-card-head">
            <h3>Revenue by company</h3>
            <input
              type="search"
              placeholder="Search name / email…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(0); }}
            />
          </div>
          <table className="qrt-table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Count plans</th>
                <th>Name</th>
                <th className="r">Wallet</th>
                <th className="r">Wallet +GST&amp;fee</th>
                <th className="r">Plans</th>
                <th className="r">Plans +GST&amp;fee</th>
                <th className="r">Actual profit</th>
                <th className="r">If leftover as {scenario.replace("_", " ")}</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => (
                <React.Fragment key={row.user_id}>
                  <tr className={row.plans_counted ? "" : "qrt-row-muted"}>
                    <td>
                      <input
                        type="checkbox"
                        checked={row.plans_counted}
                        disabled={busyUser === row.user_id}
                        onChange={() => togglePlans(row)}
                        title={row.plans_counted
                          ? "Plan revenue is counted — untick to exclude"
                          : "Plan revenue is NOT counted"}
                      />
                    </td>
                    <td
                      className="click"
                      onClick={() => navigate(`/qrt/users/${row.user_id}`)}
                    >
                      <div style={{ fontWeight: 600 }}>{row.name}</div>
                      <div className="sub">{row.email}</div>
                    </td>
                    <td className="r">{fmtMoney(row.wallet_base)}</td>
                    <td className="r">{fmtMoney(row.wallet_paid)}</td>
                    <td className="r" style={row.plans_counted ? {} : { textDecoration: "line-through", color: "#9AA5A0" }}>
                      {fmtMoney(row.plans_base)}
                    </td>
                    <td className="r" style={row.plans_counted ? {} : { textDecoration: "line-through", color: "#9AA5A0" }}>
                      {fmtMoney(row.plans_paid)}
                    </td>
                    <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(row.profit)}</td>
                    <td className="r">{fmtMoney(row.leftover_if)}</td>
                    <td>
                      <button
                        type="button"
                        className="camp-ico"
                        title="Wallet markup details"
                        aria-label="Wallet markup details"
                        onClick={() => setOpenInfo(openInfo === row.user_id ? null : row.user_id)}
                      >
                        <Info size={14} />
                      </button>
                    </td>
                  </tr>
                  {openInfo === row.user_id && (
                    <tr>
                      <td colSpan={9} style={{ background: "#F7FCF9" }}>
                        <div className="qrt-inline" style={{ gap: 24, fontSize: 12.5, padding: "4px 2px", flexWrap: "wrap" }}>
                          <span>Already earned on sends: <b>{fmtMoney(row.wallet_markup)}</b></span>
                          <span>Leftover wallet: <b>{fmtMoney(row.leftover)}</b></span>
                          {SCENARIOS.map((s) => {
                            const c = row.markup_categories?.[s.id];
                            if (!c) return null;
                            const on = scenario === s.id;
                            return (
                              <span key={s.id} style={on ? { fontWeight: 700 } : undefined}>
                                If leftover as {s.label.toLowerCase()}:{" "}
                                <b>{fmtMoney(row.leftover_pockets?.[s.id] ?? 0)}</b>
                                {" · "}₹{Number(c.markup_per_msg || 0).toFixed(2)}/msg
                                {c.source === "user" ? " · this user" : " · global"}
                              </span>
                            );
                          })}
                          <span className="sub">
                            Actual profit never changes with the filter. The filter only
                            prices leftover rupees that have not been spent.
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {rows && visible.length > 0 && (
                <tr>
                  <td />
                  <td style={{ fontWeight: 700 }}>Total, all companies {totals.excluded > 0 ? `(plans skipped for ${totals.excluded})` : ""}</td>
                  <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(totals.wallet_base)}</td>
                  <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(totals.wallet_paid)}</td>
                  <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(totals.plans_base)}</td>
                  <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(totals.plans_paid)}</td>
                  <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(totals.profit)}</td>
                  <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(totals.leftover_if)}</td>
                  <td />
                </tr>
              )}
              {rows && visible.length === 0 && (
                <tr><td className="empty" colSpan={9}>No revenue in this range</td></tr>
              )}
            </tbody>
          </table>
          <Pager page={page} total={visible.length} pageSize={PAGE_SIZE} onPage={setPage} />
        </div>
      </div>
    </div>
  );
};

export default QrtRevenue;
